import { useBookmarks, useCollections } from "@serophin/nottldr-shared/hooks";
import { Check, Loader2 } from "lucide-preact";
import { useState } from "preact/hooks";
import { useToast } from "../../contexts/ToastContext";
import { Modal } from "../Modal";

export type CollectionsModalProps = {
  authorAlias: string;
  articleId: string;
  isOpen: boolean;
  onClose: () => void;
};

const messageOf = (error: unknown, fallback: string): string => (error instanceof Error && error.message ? error.message : fallback);

/** Which collections hold the article, and the actions that change that; the library hooks keep the cache in step. */
const useArticleCollections = (authorAlias: string, articleId: string) => {
  const toast = useToast();
  const { collections, isLoading, create } = useCollections();
  const { collectionIdsOf, add, remove } = useBookmarks();
  const [updating, setUpdating] = useState<Set<string>>(new Set());
  const [isCreating, setIsCreating] = useState(false);
  const selected = new Set(collectionIdsOf(authorAlias, articleId));

  const toggle = async (id: string) => {
    if (updating.has(id)) {
      return;
    }
    setUpdating((previous) => new Set(previous).add(id));
    try {
      await (selected.has(id) ? remove(id, authorAlias, articleId) : add(id, authorAlias, articleId));
    } catch (error) {
      toast.error(messageOf(error, "Failed to update collection"));
    } finally {
      setUpdating((previous) => new Set([...previous].filter((other) => other !== id)));
    }
  };

  const createAndAdd = async (name: string): Promise<boolean> => {
    setIsCreating(true);
    try {
      const collection = await create(name);
      await add(collection.id, authorAlias, articleId);
      return true;
    } catch (error) {
      toast.error(messageOf(error, "Failed to create collection"));
      return false;
    } finally {
      setIsCreating(false);
    }
  };

  return { collections, isLoading, selected, updating, isCreating, toggle, createAndAdd };
};

const CreateForm = ({ isCreating, onCreate }: { isCreating: boolean; onCreate: (name: string) => Promise<boolean> }) => {
  const [name, setName] = useState("");

  const submit = async (event: Event) => {
    event.preventDefault();
    if (name.trim() && (await onCreate(name.trim()))) {
      setName("");
    }
  };

  return (
    <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
      <form onSubmit={submit} class="flex gap-2">
        <input
          type="text"
          value={name}
          onInput={(event) => setName(event.currentTarget.value)}
          placeholder="New collection name"
          disabled={isCreating}
          class="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-neutral-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={isCreating || !name.trim()}
          class="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isCreating ? <Loader2 size={16} class="animate-spin" /> : "Create"}
        </button>
      </form>
    </div>
  );
};

export const CollectionsModal = ({ authorAlias, articleId, isOpen, onClose }: CollectionsModalProps) => {
  const state = useArticleCollections(authorAlias, articleId);

  return (
    <Modal isOpen={isOpen} onClose={onClose} contentClass="p-0" testId="collections-modal">
      <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Add to collections</h3>
      </div>
      <div class="py-2 max-h-96 overflow-y-auto">
        {state.isLoading ? (
          <div class="flex items-center justify-center py-8">
            <Loader2 size={24} class="animate-spin text-gray-400" />
          </div>
        ) : (
          state.collections.map((collection) => (
            <button
              type="button"
              key={collection.id}
              onClick={() => state.toggle(collection.id)}
              disabled={state.updating.has(collection.id)}
              class="w-full flex items-center justify-between px-6 py-3 hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors disabled:opacity-50"
            >
              <span class="text-sm text-gray-900 dark:text-white truncate">{collection.name}</span>
              {state.selected.has(collection.id) && <Check size={16} class="flex-shrink-0 text-blue-600 dark:text-blue-400" />}
            </button>
          ))
        )}
      </div>
      <CreateForm isCreating={state.isCreating} onCreate={state.createAndAdd} />
    </Modal>
  );
};
